import { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { api, ApiError } from "../api";
import { Button, Field, Spinner } from "./ui";
import type { QaLoginTemporaryPermit } from "../qa-login-permit-types";
import "./captcha-field.css";

type Purpose = "login" | "register" | "password";
type Challenge = { enabled: false; temporaryPermit?: QaLoginTemporaryPermit } | { enabled: true; id: string; image: string; expiresAt: string };
type Loaded = { context: string; challenge: Challenge; permitDeadline?: number };
type LoginContext = { username: string; role: "user" | "admin" };

/** Shared challenge lifecycle; answers exist only in component memory. */
export function useCaptcha(purpose: Purpose, active = true, loginContext?: LoginContext) {
  const eligibleContext = purpose === "login" && loginContext?.role === "user" && loginContext.username.trim().toLowerCase() === "test888";
  const context = `${purpose}:${eligibleContext ? "user:test888" : "standard"}`;
  const currentContext = useRef(context); currentContext.current = context;
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [answer, setAnswer] = useState(""); const [error, setError] = useState("");
  const [loading, setLoading] = useState(active); const [now, setNow] = useState(Date.now());
  const [retryAt, setRetryAt] = useState(0); const [pendingRefresh, setPendingRefresh] = useState(false);
  const input = useRef<HTMLInputElement>(null); const request = useRef<AbortController | null>(null);
  const retryAtRef = useRef(0); const alive = useRef(true);
  const challenge = active && loaded?.context === context ? loaded.challenge : null;
  const permit = challenge?.enabled === false ? challenge.temporaryPermit : undefined;
  const permitRemaining = permit && loaded?.permitDeadline !== undefined ? Math.max(0, Math.ceil((loaded.permitDeadline - performance.now()) / 1000)) : 0;
  const remaining = Math.max(0, Math.ceil((retryAt - now) / 1000));
  const expired = Boolean((challenge?.enabled && Date.parse(challenge.expiresAt) <= now) || (permit && permitRemaining === 0));
  const ready = active && Boolean(challenge) && !expired && !loading && remaining === 0;
  const refresh = useCallback(async (keepError = false) => {
    request.current?.abort(); setLoaded(null); setAnswer("");
    if (!keepError) setError("");
    if (!active) { setLoading(false); return; }
    if (Date.now() < retryAtRef.current) { setPendingRefresh(true); setLoading(false); return; }
    const controller = new AbortController(); request.current = controller; setLoading(true);
    const started = performance.now();
    try {
      const data = await api<Challenge>(`/api/security/challenge?purpose=${purpose}${eligibleContext ? "&username=test888&role=user" : ""}`, { cache: "no-store", signal: AbortSignal.any([controller.signal, AbortSignal.timeout(12000)]) });
      if (typeof data.enabled !== "boolean" || (data.enabled && (!data.id || !/^data:image\/svg\+xml;base64,[A-Za-z0-9+/=]+$/.test(data.image) || data.image.length > 400000 || !Number.isFinite(Date.parse(data.expiresAt))))) throw new Error("Invalid challenge");
      let permitDeadline: number | undefined;
      if (!data.enabled && data.temporaryPermit) {
        const value = data.temporaryPermit;
        const strictTime = (at: string) => typeof at === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(at) && Number.isFinite(Date.parse(at));
        const ttl = Date.parse(value.expiresAt) - Date.parse(value.serverTime);
        if (!eligibleContext || value.username !== "test888" || !strictTime(value.serverTime) || !strictTime(value.expiresAt) || ttl <= 0 || ttl > 30 * 60_000) throw new Error("Invalid temporary permit");
        permitDeadline = started + ttl;
        if (permitDeadline <= performance.now()) throw new Error("Expired temporary permit");
      }
      if (alive.current && !controller.signal.aborted && request.current === controller && currentContext.current === context) { setLoaded({ context, challenge: data, permitDeadline }); setNow(Date.now()); }
    } catch (reason) {
      if (!alive.current || controller.signal.aborted || request.current !== controller || currentContext.current !== context) return;
      if (reason instanceof ApiError && reason.status === 429) {
        const until = Date.now() + (reason.retryAfterSeconds ?? 60) * 1000;
        retryAtRef.current = until; setRetryAt(until); setNow(Date.now());
      }
      setError(reason instanceof ApiError ? reason.message : "验证码加载失败，请重试。其他输入已保留。");
    } finally { if (alive.current && !controller.signal.aborted && request.current === controller && currentContext.current === context) setLoading(false); }
  }, [purpose, active, eligibleContext, context]);
  useEffect(() => {
    alive.current = true; setPendingRefresh(false); void refresh();
    return () => { alive.current = false; request.current?.abort(); };
  }, [refresh]);
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [active]);
  useEffect(() => {
    if (active && pendingRefresh && remaining === 0) { setPendingRefresh(false); void refresh(true); }
  }, [active, pendingRefresh, remaining, refresh]);
  useEffect(() => {
    if (permit && expired && !loading) void refresh();
  }, [permit, expired, loading, refresh]);
  function submission(): { captcha?: { id: string; answer: string } } | null {
    if (!ready || (permit && (loaded?.permitDeadline ?? 0) <= performance.now()) || (challenge?.enabled && Date.parse(challenge.expiresAt) <= Date.now())) {
      setError(remaining ? "操作过于频繁，请等待后重试。" : expired ? "验证码已过期，请换一张后重试。" : "请先加载有效的验证码。"); input.current?.focus(); return null;
    }
    if (challenge?.enabled === false) return {};
    if (!challenge || !answer.trim()) { setError("请输入图形验证码。"); input.current?.focus(); return null; }
    return { captcha: { id: challenge.id, answer: answer.trim() } };
  }
  /** Every dispatched protected request spends its challenge, even on business errors. */
  function recover(reason: unknown) {
    const handled = reason instanceof ApiError && (reason.field === "captcha" || reason.field?.startsWith("captcha.") || reason.status === 429);
    setAnswer(""); setLoaded(null);
    setError(handled && reason instanceof ApiError ? reason.message : "");
    if (reason instanceof ApiError && reason.status === 429) {
      const until = Date.now() + (reason.retryAfterSeconds ?? 60) * 1000;
      retryAtRef.current = until; setRetryAt(until); setNow(Date.now()); setPendingRefresh(true); setLoading(false);
    } else void refresh(true);
    return Boolean(handled);
  }
  function invalidate() { request.current?.abort(); setLoaded(null); setAnswer(""); setError(""); }
  function imageFailed() { setLoaded(null); setAnswer(""); setError("验证码图片无法显示，请重新加载。其他输入已保留。"); }
  return { challenge, permit, permitRemaining, answer, error: error || (expired ? "验证码已过期，请换一张后重试。" : ""), loading, remaining, ready, input, refresh, recover, invalidate, imageFailed,
    submission, setAnswer: (value: string) => { setAnswer(value); setError(""); } };
}

export function CaptchaField({ captcha, disabled = false }: { captcha: ReturnType<typeof useCaptcha>; disabled?: boolean }) {
  if (captcha.challenge?.enabled === false) return captcha.permit ? <div className="captcha-control" aria-label="临时测试登录许可">
    <strong>test888 · 临时测试许可</strong>
    <p className="captcha-control__retry">仅当前浏览器的会员登录免图形验证码，仍需正确密码并保留频率限制。注册、改密和管理员验证不变，不免除任何资金权限。</p>
    <p className="captcha-control__retry">服务器校时剩余 <strong>{Math.floor(captcha.permitRemaining / 60)}分{String(captcha.permitRemaining % 60).padStart(2, "0")}秒</strong>；到期或撤销将终止关联许可会话。以服务器校验为准。</p>
  </div> : null;
  return <div className="captcha-control" aria-label="防刷验证">
    <div className="captcha-control__image-row">
      <div className="captcha-control__image">{captcha.challenge?.enabled ? <img key={captcha.challenge.id} src={captcha.challenge.image} alt="用于防刷验证的字符图片" width="180" height="64" onError={captcha.imageFailed} /> : captcha.loading ? <Spinner label="正在加载验证码" /> : <span>验证码待加载</span>}</div>
      <Button type="button" intent="neutral" emphasis="outline" disabled={disabled || captcha.loading || captcha.remaining > 0} onClick={() => void captcha.refresh()}><RefreshCw size={15} aria-hidden="true" />{captcha.challenge ? "换一张" : "重新加载"}</Button>
    </div>
    <Field ref={captcha.input} label="图形验证码" value={captcha.answer} maxLength={12} autoComplete="off" autoCapitalize="none" spellCheck={false} disabled={disabled || !captcha.ready} error={captcha.error || undefined} hint="填写图片中的字符；看不清可换一张。验证码仅用于防刷，不是短信或实名核验。" onChange={event => captcha.setAnswer(event.target.value)} />
    {captcha.remaining > 0 && <p className="captcha-control__retry">{captcha.remaining} 秒后可重试，请勿连续提交。</p>}
  </div>;
}
